/**
 * ButterUI — Theme Registry
 *
 * Single source of truth for all designer-crafted color palettes.
 * Each theme maps shade names (25–950) to hex values.
 *
 * The purple palette matches `butter.brand.*` from tailwind.preset.ts —
 * it IS the official Butter brand color and serves as the default theme.
 *
 * These palettes are hand-tuned by the designer, NOT computed.
 * Do not generate shades algorithmically — the designer intentionally
 * chose specific saturation/hue shifts at each shade level.
 *
 * To add a new theme:
 * 1. Designer provides the full 25–950 ramp. Shade 35 is a non-standard stop
 *    the designer rarely supplies — interpolate it between 25 and 50 and say so
 *    in a trailing comment, as every palette below does.
 * 2. Name it for the TENANT HANDLE — `hivegolf`, `bunkerswv`, `samsgolf`. This
 *    matches `/add-whitelabel-tenant` and every palette added since early 2026.
 *    (This block used to say the opposite — name for the colour, `amber`/`navy` —
 *    while the skill said handle and the skill was what people actually followed.
 *    The two are now reconciled in favour of the handle.) If the handle exceeds
 *    20 characters it will not fit `tenants.color_theme` (`varchar(20)`), so
 *    shorten it and say so here — precedent: handle `thegreenroomigc` uses theme
 *    `greenroom`.
 * 3. Anchor the ramp so that shade 600 clears WCAG AA (4.5:1) against white.
 *    600 is the CTA stop — `.btn-primary`, `.btn-outline`'s border, `.btn-link`
 *    and the focus ring all read it (see `styles/components.css`) — so a palette
 *    whose 600 fails ships an unreadable button. Designers mark a `main` wherever
 *    the brand colour happens to fall, which is often 400 or 500 and far too
 *    light. Slide the whole ramp down until the first AA-passing shade lands on
 *    600, interpolating the ends where the source runs out, and record in the
 *    palette's docblock what was dropped and what was extrapolated. Do NOT slide
 *    a ramp whose 600 already passes — that only makes it lighter than drawn.
 *    Beyond that anchoring, do not generate shades algorithmically; the stops are
 *    hand-tuned.
 * 4. Add it to the `themes` map below
 * 5. Add the name to the `ThemeName` union in @lgc/database (tenant/constraints.ts),
 *    in the SAME commit — `themes` is typed `Record<ThemeName, ThemePalette>`, so
 *    a name without a palette (or vice versa) is a typecheck failure. That's
 *    deliberate: the two files cannot silently drift.
 * 6. That's it — CSS vars + Tailwind mapping pick it up automatically
 *
 * If the theme will be used on the dark branded canvas, check its glow stop in
 * `glow.ts` (the default 500 is wrong for dark or monochrome ramps) and its CTA
 * tone in `ctaTone.ts`. Both are pinned to the Figma frames by tests.
 *
 * @see packages/ui/docs/ColorTheming.mdx for architecture details
 * @see packages/ui/src/components/data-display/TenantLogo/registry.ts
 */

import type { ThemeName } from '@lgc/database/types';

// ── Shade scale ─────────────────────────────────────────────────────

/**
 * Shade 35 is a non-standard stop introduced by the grey palette from the
 * designer. We include it globally so every palette has the same keys —
 * this keeps `ThemePalette` a simple `Record` and ensures `applyTheme()`
 * always emits the same set of CSS custom properties regardless of theme.
 */
export const THEME_SHADES = [25, 35, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;
export type ThemeShade = (typeof THEME_SHADES)[number];

// ── Types ───────────────────────────────────────────────────────────

/** A full color ramp from 25 (lightest) to 950 (darkest). */
export type ThemePalette = Record<ThemeShade, string>;

/** Re-export ThemeName from @lgc/database (source of truth). */
export type { ThemeName } from '@lgc/database/types';

// ── Palettes ────────────────────────────────────────────────────────

export const themes: Record<ThemeName, ThemePalette> = {
  /**
   * Purple — Butter brand default.
   * Matches `butter.brand.*` from tailwind.preset.ts (25–800).
   * 900/950 are extrapolated — get official values from designer.
   */
  purple: {
    25:  '#FCFAFF',  // butter.brand.25
    35:  '#FBF8FF',  // TODO: get from designer (interpolated between 25/50)
    50:  '#F9F5FF',  // butter.brand.50
    100: '#F4EBFF',  // butter.brand.100
    200: '#E9D7FE',  // butter.brand.200
    300: '#D6BBFB',  // butter.brand.300
    400: '#B692F6',  // butter.brand.400
    500: '#9E77ED',  // butter.brand.500
    600: '#7F56D9',  // butter.brand.600
    700: '#6941C6',  // butter.brand.700
    800: '#53389E',  // butter.brand.800
    900: '#3B0764',  // TODO: get from designer (extrapolated)
    950: '#2C0550',  // TODO: get from designer (extrapolated)
  },

  /**
   * Green — First white-label theme.
   * All values from designer's "Primary Green" palette.
   *
   * ⚠ CONTRAST: 600 is 3.82:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. Its designer ramp predates the 600-anchoring rule above.
   * Deliberately NOT re-anchored here, but unlike the other flagged palettes this
   * one may be free to fix: no tenant carried `green` in the 2026-09-21 production
   * export. Re-check that before sliding it — the export was a snapshot, and
   * staging holds a different tenant set. `linksindoor` below is this same ramp
   * anchored the correct way, and is the reference for what the fix looks like.
   */
  green: {
    25:  '#F8FDF9',
    35:  '#F5FCF7',  // TODO: get from designer (interpolated between 25/50)
    50:  '#F1FCF4',
    100: '#DFF9E9',
    200: '#C1F1D3',
    300: '#90E5B1',
    400: '#59CF87',
    500: '#30AD61',
    600: '#249551',
    700: '#207542',
    800: '#1E5D38',
    900: '#1A4D2F',
    950: '#092A18',
  },

  /**
   * Grey — Neutral/monochrome theme.
   * All values from designer's grey palette.
   * Note: shade 35 originates from this palette; other palettes have
   * interpolated values at that stop.
   */
  grey: {
    25:  '#FCFCFC',
    35:  '#FAFAFA',
    50:  '#F7F7F7',
    100: '#F5F5F5',
    200: '#E5E5E5',
    300: '#D6D6D6',
    400: '#A3A3A3',
    500: '#737373',
    600: '#525252',
    700: '#424242',
    800: '#292929',
    900: '#141414',
    950: '#0F0F0F',
  },
  /**
   * Greenroom IGC — dark forest-green white-label theme.
   * All values from designer's palette.
   */
  greenroom: {
    25:  '#F2F7F4',
    35:  '#EBF3EF',  // TODO: get from designer (interpolated between 25/50)
    50:  '#E5EFEA',
    100: '#CCDFD5',
    200: '#99BEAB',
    300: '#679E80',
    400: '#347D56',
    500: '#015D2C',
    600: '#014A23',
    700: '#01381A',
    800: '#002512',
    900: '#001E0E',
    950: '#001309',
  },

  /**
   * Tap & Tees — navy-blue white-label theme.
   * All values from designer's palette (500 is the brand "main").
   */
  tapandtees: {
    25:  '#F7F9FD',
    35:  '#F3F6FC',  // TODO: get from designer (interpolated between 25/50)
    50:  '#EEF3FA',
    100: '#DCE6F4',
    200: '#BCD0E8',
    300: '#92B1D8',
    400: '#5F84BC',
    500: '#2E5591',
    600: '#274A80',
    700: '#213F6C',
    800: '#1A3257',
    900: '#12233E',
    950: '#09111F',
  },

  /**
   * Virtual Clubhouse Golf — neutral/monochrome white-label theme.
   * All values from designer's "Neutral" palette.
   */
  virtualclubhousegolf: {
    25:  '#F8F8F8',
    35:  '#F4F4F4',  // TODO: get from designer (interpolated between 25/50)
    50:  '#F0F0F0',
    100: '#DFDFDF',
    200: '#C0C0C0',
    300: '#979797',
    400: '#606060',
    500: '#202020',
    600: '#1C1C1C',
    700: '#171717',
    800: '#131313',
    900: '#0E0E0E',
    950: '#070707',
  },

  /**
   * Hive Golf — amber/gold white-label theme.
   * All values from designer's palette (500 is the brand "main").
   *
   * ⚠ CONTRAST: 600 is 3.04:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. Its designer ramp predates the 600-anchoring rule above.
   * Deliberately NOT re-anchored: this palette is live, and sliding it would
   * recolour a shipping tenant. Fix needs its own PR and a QA pass.
   */
  hivegolf: {
    25:  '#FFFEF4',
    35:  '#FFFDEE',  // TODO: get from designer (interpolated between 25/50)
    50:  '#FFFCE7',
    100: '#FFF8C1',
    200: '#FFED86',
    300: '#FFDB41',
    400: '#FFC60D',
    500: '#F5AE00',
    600: '#D18200',
    700: '#A65B02',
    800: '#89470A',
    900: '#743A0F',
    950: '#441D04',
  },

  /**
   * The Links Club — olive/forest-green white-label theme.
   * All values from designer's "Primary Gold" palette (500 is the brand "main").
   *
   * ⚠ CONTRAST: 600 is 3.97:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. It was already slid one stop, which was not enough.
   * Deliberately NOT re-anchored: this palette is live, and sliding it would
   * recolour a shipping tenant. Fix needs its own PR and a QA pass.
   *
   * Transcription note: stops 25–400 are the Figma column verbatim, while 600–900
   * are its 500–800 slid down one (so the designer's main #668B25 sits on 600) and
   * 950 is anchored to the Figma 950. That leaves 500 with no source value, so it
   * is the midpoint of 400 and 600 — not a missing hex. Figma's own 900 (#31431A)
   * is unused, which is the unavoidable cost of a one-stop slide across 12 stops.
   */
  thelinksclub: {
    25:  '#F7FAEB',
    35:  '#F1F7E0',  // TODO: get from designer (interpolated between 25/50)
    50:  '#EAF4D3',
    100: '#D8EBAB',
    200: '#BCDC7A',
    300: '#A1CA51',
    400: '#8AB835',
    500: '#7A9E2E',  // interpolated between 400 and 600 (no source stop — see docblock)
    600: '#668B25',
    700: '#537121',
    800: '#455E20',
    900: '#3C501F',
    950: '#1B280B',
  },

  /**
   * Swing 365 — teal white-label theme.
   * All values from designer's color scale (500 is the brand "main").
   * Shade 35 is interpolated between 25 and 50 — get official value from designer.
   *
   * ⚠ CONTRAST: 600 is 4.27:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. Its designer ramp predates the 600-anchoring rule above.
   * Deliberately NOT re-anchored: this palette is live, and sliding it would
   * recolour a shipping tenant. Fix needs its own PR and a QA pass.
   */
  swing365: {
    25:  '#F7FDFD',
    35:  '#F3FCFC',  // TODO: get from designer (interpolated between 25/50)
    50:  '#EFFCFC',
    100: '#D8F5F5',
    200: '#B5EAEC',
    300: '#81DBDF',
    400: '#47C2C9',  // main from logo
    500: '#2BA6AF',  // main
    600: '#278693',
    700: '#266D78',
    800: '#265A64',
    900: '#244C55',
    950: '#133139',
  },

  /**
   * SL Golf Lounge — near-black/neutral white-label theme.
   * Brand Black (main): #231F20. Full neutral scale from designer.
   * Shade 35 is interpolated between 25 and 50 — get official value from designer.
   */
  slgolflounge: {
    25:  '#FAFAFA',
    35:  '#F8F8F8',  // TODO: get from designer (interpolated between 25/50)
    50:  '#F4F4F4',
    100: '#E2E2E2',
    200: '#C9C9C9',
    300: '#A3A3A3',
    400: '#787878',
    500: '#5C5C5C',
    600: '#4C4C4C',
    700: '#3E3E3E',
    800: '#353535',
    900: '#2E2E2E',
    950: '#010101',
  },

  /**
   * Swingshaft — dark navy/steel-blue white-label theme.
   * Based on designer's "Original Scale" palette (500 is the brand "main").
   * Shade 35 is interpolated between 25 and 50 — get official value from designer.
   */
  /**
   * West Texas Golf — deep crimson red white-label theme.
   * Primary Red (main): #A9040C. Full scale from designer.
   * Shade 35 is interpolated between 25 and 50.
   */
  westtexasgolf: {
    25:  '#FAE4E5',
    35:  '#F8D0D2',  // interpolated between 25/50
    50:  '#F5C2C4',
    100: '#EDADB0',
    200: '#DC8387',
    300: '#CB585E',
    400: '#BA2E35',
    500: '#A9040C',  // main
    600: '#87030A',
    700: '#650207',
    800: '#440205',
    900: '#220102',
    950: '#110001',
  },

  swingshift: {
    25:  '#F6F9FB',
    35:  '#F2F6F9',  // TODO: get from designer (interpolated between 25/50)
    50:  '#EBF2F7',
    100: '#D5E4ED',
    200: '#ADCADD',
    300: '#77A8C6',
    400: '#3F7597',
    500: '#1C3443',
    600: '#182D3A',
    700: '#152631',
    800: '#111F28',
    900: '#0C161C',
    950: '#060B0E',
  },

  /**
   * Next Level Golf — teal-green white-label theme.
   * Primary teal (main): #2D9C8C. Full ramp from designer's "Color Logo (main)" palette.
   * Shade 35 is interpolated between 25 and 50 — get official value from designer.
   */
  nextlevelgolf: {
    25:  '#F7FCF9',
    35:  '#F4FBF7',  // interpolated between 25/50
    50:  '#EFFAF4',
    100: '#DBF3E2',
    200: '#B4E6CA',
    300: '#83D2A8',
    400: '#47AE7E',
    500: '#2D9C8C',  // main
    600: '#1E7D55',
    700: '#186446',
    800: '#155039',
    900: '#17302D',
    950: '#132E21',
  },

  /**
   * Fairway365 — primary green.
   *
   * ⚠ CONTRAST: 600 is 4.43:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. Its designer ramp predates the 600-anchoring rule above.
   * Deliberately NOT re-anchored: this palette is live, and sliding it would
   * recolour a shipping tenant. Fix needs its own PR and a QA pass.
   */
  fairway365: {
    25:  '#FCFFFC',
    35:  '#F5FBF5',  // interpolated between 25/50
    50:  '#EEF6EE',
    100: '#DDEDDD',
    200: '#BBDCBB',
    300: '#99CA99',
    400: '#77B977',
    500: '#55A755',  // main
    600: '#448644',
    700: '#336433',
    800: '#224322',
    900: '#112111',
    950: '#091109',
  },

  /**
   * Perfect Par Golf Studio — terracotta/copper white-label theme.
   * Re-anchored +1 (2026-09): the ramp previously carried the designer's main at
   * 700, which left 600 — the stop every button actually paints — holding
   * #B46B4E at 4.07:1 on white, a colour nobody chose and below WCAG AA. The
   * whole ramp is slid one stop so the intended #98583F sits on 600. Dropped
   * from the light end: 25=#FEFAF8. Extrapolated past the designer's darkest
   * stop: 950=#1E0F0A.
   * Shade 35 is interpolated between 25 and 50.
   */
  perfectpargolfstudio: {
    25:  '#FBF7F5',
    35:  '#FAF2EE',  // interpolated between 25/50
    50:  '#F8EDE8',
    100: '#F2DED6',
    200: '#E9C6B8',
    300: '#D4967D',
    400: '#C9856A',
    500: '#B46B4E',
    600: '#98583F',  // main — 5.52:1 on white
    700: '#7D4B37',
    800: '#694333',
    900: '#382017',
    950: '#1E0F0A',  // extrapolated — confirm with designer
  },

  /**
   * Draper Indoor Golf — bright green white-label theme.
   * Primary brand (main): #39B04E. Full ramp from designer's palette.
   * Shade 35 is interpolated between 25 and 50.
   *
   * ⚠ CONTRAST: 600 is 4.08:1 against white — below the WCAG AA 4.5:1 this
   * repo commits to in StyleAndA11y.mdx, and 600 is the stop every button,
   * link and focus ring paints. Its designer ramp predates the 600-anchoring rule above.
   * Deliberately NOT re-anchored: this palette is live, and sliding it would
   * recolour a shipping tenant. Fix needs its own PR and a QA pass.
   */
  draperindoorgolf: {
    25:  '#F8FCF8',
    35:  '#F5FBF5',  // interpolated between 25/50
    50:  '#F2FBF3',
    100: '#E0F8E3',
    200: '#C3EFCA',
    300: '#95E0A2',
    400: '#5ECA71',
    500: '#39B04E',  // main
    600: '#29903B',
    700: '#247132',
    800: '#215A2C',
    900: '#1D4A26',
    950: '#0B2811',
  },

  /**
   * Bunkers — green.
   * Source: Figma "Color Scale - options" node 14054:409929, Bunkers Club - Primary Brand
   * (green)
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 600: from logo.
   */
  bunkerswv: {
    25:  '#FAFCF9',
    35:  '#F8FBF6',  // interpolated between 25/50
    50:  '#F5FAF3',
    100: '#E7F4E4',
    200: '#D0E8CA',
    300: '#ACD4A1',
    400: '#7FB870',
    500: '#5C9B4C',
    600: '#49803B',
    700: '#3B6431',
    800: '#31512A',
    900: '#2A4324',
    950: '#132310',
  },

  /**
   * Casey's Indoor Golf Club — olive.
   * Source: Figma "Color Scale - options" node 14177:579772, row "Primary Brand" (olive).
   * The column carries two identically-titled ramps; this is the one the designer tagged
   * "from logo", and it matches the logo art.
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FAFCF8.
   * Extrapolated past the designer's darkest stop: 950=#0D1604.
   * Designer notes — 600: from logo.
   */
  caseysindoorgolfclub: {
    25:  '#F6FAEB',
    35:  '#F0F7DF',  // interpolated between 25/50
    50:  '#E9F4D3',  // extrapolated — confirm with designer
    100: '#D6EBAB',
    200: '#B9DC7A',
    300: '#9ECB50',
    400: '#80B032',
    500: '#6D9B28',
    600: '#4C6B20',
    700: '#3E561E',
    800: '#36491E',
    900: '#1A280B',
    950: '#0D1604',  // extrapolated — confirm with designer
  },

  /**
   * G-Man Golf — red.
   * Source: Figma "Color Scale - options" node 14020:609691, G-Man - Primary (red)
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FFF8F8.
   * Extrapolated past the designer's darkest stop: 950=#250304.
   * Designer notes — 600: main from logo; 700: can be used for buttons.
   */
  gmangolfsim: {
    25:  '#FEF2F2',
    35:  '#FEEAEA',  // interpolated between 25/50
    50:  '#FEE2E3',  // extrapolated — confirm with designer
    100: '#FECACB',
    200: '#FCA5A7',
    300: '#F97073',
    400: '#F04347',
    500: '#E03A3E',
    600: '#BA1B1F',
    700: '#9A1A1D',
    800: '#7F1D1F',
    900: '#450A0B',
    950: '#250304',  // extrapolated — confirm with designer
  },

  /**
   * Iron Eagle Golf — orange.
   * Source: Figma "Color Scale - options" node 14409:677609, row "Primary Brand from
   * website #2" (orange). The column offers a green #1 and this orange #2; orange was
   * chosen.
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FFFBF6.
   * Extrapolated past the designer's darkest stop: 950=#220805.
   * Designer notes — 500: main from website.
   */
  golfironeagle: {
    25:  '#FEF6EE',
    35:  '#FDF0E3',  // interpolated between 25/50
    50:  '#FCEBD8',  // extrapolated — confirm with designer
    100: '#F9D3AF',
    200: '#F5B37C',
    300: '#EF8948',
    400: '#EB6A24',
    500: '#DC511A',
    600: '#B73D17',
    700: '#92311A',
    800: '#752B19',
    900: '#3F130B',
    950: '#220805',  // extrapolated — confirm with designer
  },

  /**
   * Greenside Golf Club — green.
   * Source: Figma "Color Scale - options" node 14334:522215, row "Accent green (from
   * website - used for buttons)". The brand mark itself is black.
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#F8FEF9.
   * Extrapolated past the designer's darkest stop: 950=#041608.
   * Designer notes — 400: from website; 600: can be used for buttons.
   */
  greensidegolfclub: {
    25:  '#F1FCF3',
    35:  '#E8FAEC',  // interpolated between 25/50
    50:  '#E0F8E4',  // extrapolated — confirm with designer
    100: '#C2F0CA',
    200: '#92E3A0',
    300: '#6BD27E',
    400: '#35B24C',
    500: '#26933A',
    600: '#217431',
    700: '#1F5C2B',
    800: '#1B4C25',
    900: '#0A2911',
    950: '#041608',  // extrapolated — confirm with designer
  },

  /**
   * Indoor Golf Club — monochrome.
   * Source: Figma "Color Scale - options" node 14280:687222, row "Neutral". "Primary
   * Black" is a single #111111 swatch, so the neutral scale is the only full ramp.
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 500(brand): #111111 single swatch.
   */
  indoorgolfclub: {
    25:  '#F8F8F8',
    35:  '#F6F6F6',  // interpolated between 25/50
    50:  '#F3F3F3',
    100: '#E6E6E6',
    200: '#CDCDCD',
    300: '#ADADAD',
    400: '#818181',
    500: '#4E4E4E',
    600: '#414141',
    700: '#323232',
    800: '#232323',
    900: '#111111',
    950: '#000000',
  },

  /**
   * Links Indoor — green.
   * Source: Figma row "Row Brand color" supplied 2026-09-21 for the linksindoor
   * tenant. The 12 stops are byte-identical to the `green` palette above; this is
   * a separate entry rather than a reuse because Links Indoor previously shared
   * the `thelinksclub` theme with The Links Club (a different tenant), and
   * pointing it at a shared palette is what coupled them in the first place.
   * Re-anchored +1: as drawn, 600 (#249551) was 3.82:1 on white, below WCAG AA,
   * so the ramp is slid until #207542 (5.70:1) sits on 600. Dropped from the
   * light end: 25=#F8FDF9. Extrapolated past the designer's darkest stop:
   * 950=#03170C.
   */
  linksindoor: {
    25:  '#F1FCF4',
    35:  '#E8FAEE',  // interpolated between 25/50
    50:  '#DFF9E9',
    100: '#C1F1D3',
    200: '#90E5B1',
    300: '#59CF87',
    400: '#30AD61',
    500: '#249551',
    600: '#207542',
    700: '#1E5D38',
    800: '#1A4D2F',
    900: '#092A18',
    950: '#03170C',  // extrapolated — confirm with designer
  },

  /**
   * Meridiana Golf Swing — gold.
   * Source: Figma "Color Scale - options" node 13767:587820, row "Primary Gold". Note the
   * enclosing Figma frame is mis-titled "Swing Path Golf Club - scale"; the inner title
   * and logo are Meridiana's.
   * Re-anchored +2: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FEFDF9,
   * 50=#FCF9EF. Extrapolated past the designer's darkest stop: 900=#0D0803, 950=#050301.
   * Designer notes — 500: from website.
   */
  meridianagolfswing: {
    25:  '#F8F2DA',
    35:  '#F6ECCC',  // interpolated between 25/50
    50:  '#F3E7BF',  // extrapolated — confirm with designer
    100: '#EBD89E',
    200: '#E1C579',
    300: '#D4AE51',
    400: '#D1A542',
    500: '#BE8E2E',
    600: '#946B23',
    700: '#614216',
    800: '#241708',
    900: '#0D0803',  // extrapolated — confirm with designer
    950: '#050301',  // extrapolated — confirm with designer
  },

  /**
   * Mettle Golf — yellow.
   * Source: Figma "Color Scale - options" node 14341:631797, Mettle Golf - Primary Brand
   * from website (yellow/lime)
   * Re-anchored +2: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FDFEF5,
   * 50=#F9FBEB. Extrapolated past the designer's darkest stop: 900=#211607, 950=#130C03.
   * Designer notes — 400: original from website.
   */
  mettle: {
    25:  '#EFF5CC',
    35:  '#EAF1B4',  // interpolated between 25/50
    50:  '#E5ED9B',  // extrapolated — confirm with designer
    100: '#DAE262',
    200: '#D9DC4B',
    300: '#C9C529',
    400: '#AD9F21',
    500: '#8A761E',
    600: '#735E20',
    700: '#634F20',
    800: '#392A0F',
    900: '#211607',  // extrapolated — confirm with designer
    950: '#130C03',  // extrapolated — confirm with designer
  },

  /**
   * Sam's Golf — red.
   * Source: Figma "Color Scale - options" node 13847:609412, Sam's Club - color scale /
   * Primary
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 500: main; 600: as main for bg buttons; 700: main from logo.
   */
  samsgolf: {
    25:  '#FFF8F8',
    35:  '#FEF5F6',  // interpolated between 25/50
    50:  '#FEF2F3',
    100: '#FFE1E3',
    200: '#FFC9CC',
    300: '#FEA3A8',
    400: '#FB6E76',
    500: '#F3404A',
    600: '#E0222C',
    700: '#BF1922',
    800: '#9C181F',
    900: '#811B21',
    950: '#46090C',
  },

  /**
   * South Bay Golf Club — teal.
   * Source: Figma "Color Scale - options" node 13898:624839, South Bay Golf Club - Primary
   * (teal)
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 500: main; 600: as main for bg buttons; 950: main from logo.
   */
  sbgolfclub: {
    25:  '#F8FCFC',
    35:  '#F5FAFA',  // interpolated between 25/50
    50:  '#F2F9F9',
    100: '#DEEFEF',
    200: '#C1DEE0',
    300: '#96C6CA',
    400: '#63A5AD',
    500: '#488992',
    600: '#3E727C',
    700: '#375E67',
    800: '#344F56',
    900: '#2F444A',
    950: '#23383F',
  },

  /**
   * Sim Suite Golf — lime.
   * Source: Figma "Color Scale - options" node 14054:409655, row "Primary Brand" (lime).
   * The sibling "Accent from logo" row is only two swatches (25 + a near-black teal 950),
   * not a scale.
   * Re-anchored +2: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#FBFDF5,
   * 50=#F8FCE9. Extrapolated past the designer's darkest stop: 900=#0F1803, 950=#080D01.
   * Designer notes — 500: from logo.
   */
  simsuitegolf: {
    25:  '#EFF8CF',
    35:  '#E7F4BA',  // interpolated between 25/50
    50:  '#DFF1A5',  // extrapolated — confirm with designer
    100: '#CFEA7D',
    200: '#C3E267',
    300: '#B7DB57',
    400: '#96BF32',
    500: '#70961A',
    600: '#567318',
    700: '#3C4E19',
    800: '#1E2B08',
    900: '#0F1803',  // extrapolated — confirm with designer
    950: '#080D01',  // extrapolated — confirm with designer
  },

  /**
   * Smash Factor 150 — neon green.
   * Source: Figma "Color Scale - options" node 14335:522855, row "Accent green (from
   * logo)". The brand mark itself is black.
   * Re-anchored +2: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#F6FFFA,
   * 50=#ECFFF5. Extrapolated past the designer's darkest stop: 900=#002410, 950=#001609.
   */
  'smashfactor-150': {
    25:  '#D2FFEB',
    35:  '#BDFFE1',  // interpolated between 25/50
    50:  '#A8FFD7',  // extrapolated — confirm with designer
    100: '#65FFBB',
    200: '#1BFF96',
    300: '#00F976',
    400: '#00D05E',
    500: '#00A24D',
    600: '#007E41',
    700: '#006236',
    800: '#003B1D',
    900: '#002410',  // extrapolated — confirm with designer
    950: '#001609',  // extrapolated — confirm with designer
  },

  /**
   * Swing18 — green.
   * Source: Figma "Color Scale - options" node 14134:564298, Swing18 Club - Primary Brand
   * (green)
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 500: from logo; 600: can be used for buttons.
   * CONFLICT: shade 400: swatch FILL is #7FB870 (identical to Bunkers' 400 - likely a
   * stale copy) but the Hex LABEL reads 7ABE5D. Interpolating 300->500 gives ~#7EBF63,
   * which matches the LABEL. Taking 7ABE5D; flag for designer.
   */
  'swing18-tab': {
    25:  '#F9FCF8',
    35:  '#F5FAF2',  // interpolated between 25/50
    50:  '#F1F9EC',
    100: '#DFF1D6',
    200: '#C3E4B2',
    300: '#9CD284',
    400: '#7ABE5D',
    500: '#61AC43',
    600: '#45812F',
    700: '#376328',
    800: '#2F5024',
    900: '#2A4522',
    950: '#13250E',
  },

  /**
   * Swing Path Golf Club — blue.
   * Source: Figma "Color Scale - options" node 13767:586944, row "Accent Blue (from
   * website - used for buttons)". The brand mark itself is black #000000, so the accent is
   * what a button can use.
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 600: from website.
   */
  swingpathgolfclub: {
    25:  '#F6FAFD',
    35:  '#F2F6FC',  // interpolated between 25/50
    50:  '#EDF3FB',
    100: '#D8E7F7',
    200: '#B4CFEF',
    300: '#83B1E5',
    400: '#4087D7',
    500: '#20599A',
    600: '#1C4D85',
    700: '#184170',
    800: '#13345A',
    900: '#0D2540',
    950: '#071220',
  },

  /**
   * Tee To Green — green.
   * Source: Figma "Color Scale - options" node 14020:609554, Tee To Green Club - Primary
   * Brand
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#F9FCF6.
   * Extrapolated past the designer's darkest stop: 950=#0A1306.
   * Designer notes — 600: from website.
   */
  teetogreen: {
    25:  '#F3F9EC',
    35:  '#EBF5E1',  // interpolated between 25/50
    50:  '#E3F1D6',  // extrapolated — confirm with designer
    100: '#C9E4B2',
    200: '#A8D185',
    300: '#89BD5E',
    400: '#6AA240',
    500: '#558632',
    600: '#406328',
    700: '#364F25',
    800: '#2F4423',
    900: '#16240F',
    950: '#0A1306',  // extrapolated — confirm with designer
  },

  /**
   * Golf Lab (Cedar Falls) — green.
   * Source: Figma "Color Scale - options" node 13773:596032, Golf Lab - Primary Brand
   * Re-anchored +1: as drawn, 600 failed WCAG AA against white, so the ramp is slid until
   * a passing shade sits on 600 (the CTA stop). Dropped from the light end: 25=#F8FEF9.
   * Extrapolated past the designer's darkest stop: 950=#011A05.
   * Designer notes — 600: from website.
   */
  thegolflabcf: {
    25:  '#F0FDF1',
    35:  '#E6FDE8',  // interpolated between 25/50
    50:  '#DBFDDF',  // extrapolated — confirm with designer
    100: '#BAF8C1',
    200: '#83F291',
    300: '#46E25A',
    400: '#1EC935',
    500: '#12A125',
    600: '#128321',
    700: '#146720',
    800: '#12551D',
    900: '#042F0C',
    950: '#011A05',  // extrapolated — confirm with designer
  },

  /**
   * Valley Golf Simulators — taupe.
   * Source: Figma "Color Scale - options" node 14341:630583, Valley Golf Simulators -
   * Primary Brand from website (taupe/brown)
   * Transcribed as drawn — 600 already clears WCAG AA against white.
   * Designer notes — 400: original main; 600: can be used for button.
   */
  valleygolfsimulators: {
    25:  '#FBFAF7',
    35:  '#F8F6F4',  // interpolated between 25/50
    50:  '#F6F3F0',
    100: '#E8E0D9',
    200: '#D2C4B6',
    300: '#B89F8C',
    400: '#A3826C',
    500: '#94715E',
    600: '#7F5C4F',
    700: '#664942',
    800: '#583E3B',
    900: '#4D3836',
    950: '#2B1D1D',
  },
  golferslounge: {
    25:  '#FDFCF6',
    35:  '#FCFAF2',  // interpolated between 25/50
    50:  '#FAF8EC',
    100: '#F4EDCD',
    200: '#E9DA9F',
    300: '#DDC067',
    400: '#D1A73E',
    500: '#B98B2E',  // main
    600: '#A77327',
    700: '#865422',
    800: '#704523',
    900: '#603B23',
    950: '#371E11',
  },
  birdiesbaygolf: {
    25:  '#F7FCFC',
    35:  '#F4FCFC',  // interpolated between 25/50
    50:  '#EFFBFC',
    100: '#D7F3F6',
    200: '#B4E7ED',
    300: '#81D5DF',
    400: '#46B9CA',
    500: '#2A9BAD',  // main
    600: '#268094',
    700: '#256879',
    800: '#265664',
    900: '#244855',
    950: '#132E39',
  },

  /**
   * 3P Golf Sims — green.
   * Source: Figma "Color Scales" node 14889:697532, 3P Golf Sims - Primary Brand from
   * website (green). Transcribed as drawn; 600 is the designer's "main from website".
   * The designer's 600 ("main from website") is #19924A, 4.0:1 against white — below WCAG
   * AA for normal text. Product asked for AA, so 600 takes the ramp's own 700 (#197C42,
   * 5.25:1); 600 and 700 are therefore the same value. Deviates from Figma — flag for designer.
   * CONFLICT: shade 800: swatch FILL is #196238 but the Hex LABEL reads 236341. The fill
   * sits on the 700→900 ramp; the label is off-hue. Taking the FILL; flag for designer.
   */
  '3pgolfsims': {
    25:  '#F8FEF9',
    35:  '#F4FDF6',  // interpolated between 25/50
    50:  '#F0FDF4',
    100: '#DDFBE8',
    200: '#BDF5D3',
    300: '#8AEEB1',
    400: '#4FD986',
    500: '#28BF65',
    600: '#197C42',  // main — was #19924A as drawn (4.0:1); AA fix, see above
    700: '#197C42',
    800: '#196238',
    900: '#16512F',
    950: '#062D17',
  },

  /**
   * TeeUp Indoor Golf — blue.
   * Source: Figma "Color Scales" node 14889:697201, TeeUp-indoor golf - Primary Brand
   * from website (blue). Transcribed as drawn; 600 is the designer's "main from website".
   * The designer's 600 ("main from website") is #3281E2, 3.9:1 against white — below WCAG
   * AA for normal text. 600 takes the ramp's own 700 (#2669CF, 5.24:1), so the two are the
   * same value. Deviates from Figma — flag for designer. Palette only: no white-label app.
   */
  teeupindoorgolf: {
    25:  '#F8FBFF',
    35:  '#F4FAFE',  // interpolated between 25/50
    50:  '#F0F8FE',
    100: '#DDEEFC',
    200: '#C2E3FB',
    300: '#98D2F8',
    400: '#67B9F3',
    500: '#449BED',
    600: '#2669CF',  // main — was #3281E2 as drawn (3.9:1); AA fix, see above
    700: '#2669CF',
    800: '#2556A8',
    900: '#234985',
    950: '#1A2E51',
  },
  /**
   * CJ's Swing Studio — accent green (the brand black #000000 lives in the logo only).
   * Source: Figma "Color Scales" node 15630:62179, "Accent Green (from website logo)".
   * The designer marks 800 (#4A5B34) "main from logo" and 700 (#5E723D) "can be used for
   * buttons". The ramp's 600 is #78914B (3.5:1 against white, below WCAG AA), so 600 takes
   * the 700 value (5.32:1) — which is also the designer's button colour. 600 and 700 are
   * the same value. Logo-only tenant: no white-label app.
   */
  cjsswingstudio: {
    25:  '#FAFCF9',
    35:  '#F8FCF6',  // interpolated between 25/50
    50:  '#F6FBF3',
    100: '#ECF6E5',
    200: '#DAECCC',
    300: '#C1DEA7',
    400: '#A8C97D',
    500: '#91B05E',
    600: '#5E723D',  // main — was #78914B as drawn (3.5:1); AA fix, see above
    700: '#5E723D',
    800: '#4A5B34',
    900: '#3C4B2C',
    950: '#202915',
  },

  /**
   * 4ore Play Golf — gold.
   * Source: Figma "Color Scales" node 15665:60057, "Primary Brand from website #1" (gold).
   * The page also carries a green ramp (#2, main 700 #1B6710); the dev chose gold as the
   * app theme. The designer's main is 400 (#D8B756, 1.9:1 against white); the ramp's 600 is
   * #A78437 (3.5:1, below WCAG AA), so 600 takes the 700 value (#8E6C2B, 4.85:1). 600 and
   * 700 are the same value. Deviates from Figma — flag for designer.
   */
  '4oreplaygolf': {
    25:  '#FEFBF3',
    35:  '#FDF9ED',  // interpolated between 25/50
    50:  '#FCF7E7',
    100: '#F7EDD3',
    200: '#F1DEAC',
    300: '#E7CC82',
    400: '#D8B756',
    500: '#C19D45',
    600: '#8E6C2B',  // main — was #A78437 as drawn (3.5:1); AA fix, see above
    700: '#8E6C2B',
    800: '#755621',
    900: '#5D4218',
    950: '#362207',
  },
  /**
   * The Golf Barn — red.
   * Source: Figma "Color Scales" node 15850:76812, "Primary Brand" (main 500 #BE1E2D, from
   * the logo and website). 600 is #981824 (well above 4.5:1 against white), so no AA fix.
   * The page's Neutral ramp is not carried here (a palette is the brand ramp only).
   */
  'thegolf-barn': {
    25:  '#FFF6F5',
    35:  '#FFF2F1',  // interpolated between 25/50
    50:  '#FFEEEC',
    100: '#FFDBD8',
    200: '#FFB3AE',
    300: '#F68581',
    400: '#DF5152',
    500: '#BE1E2D',  // main
    600: '#981824',
    700: '#72121B',
    800: '#560E14',
    900: '#3F0A0F',
    950: '#2A070A',
  },
  /**
   * The Green Jacket — logo teal-green.
   * Source: Figma "Color Scales" node 15850:76813, "Primary Brand from logo". The designer
   * marks 800 (#02362A) "main from logo" and 600 (#2B7762, 5.4:1 against white) "can be
   * used for buttons", so theme-600 is the designer's button colour as drawn. The page also
   * carries a bright-green "Accent from website" ramp (main 500 #44B92D); the dev chose the
   * logo ramp as the app theme. Logo-only tenant: no white-label app.
   */
  thegreenjacketgolf: {
    25:  '#F9FBFA',
    35:  '#F5FAF8',  // interpolated between 25/50
    50:  '#F2F8F6',
    100: '#E2F0EB',
    200: '#C4DFD5',
    300: '#9BC9B9',
    400: '#6BAD98',
    500: '#47957E',
    600: '#2B7762',  // main (button colour per designer); logo main is 800
    700: '#115343',
    800: '#02362A',
    900: '#052B21',
    950: '#01140F',
  },
};

// ── Defaults ────────────────────────────────────────────────────────

export const DEFAULT_THEME: ThemeName = 'purple';
